import React, { useState, useRef, useEffect } from "react";
import axios, { CancelToken } from "axios";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { StatusCode } from "api/protocols";
import Sheet from "components/sheet/Sheet";
import Callout from "components/callout/Callout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Select } from "components/select/index";
import { ICustomer } from "redux/reducers/models/customer";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import "components/fileImport/fileImport.scss";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import ReactDOM from "react-dom";
import Radio from "components/radio";

interface IFileSelectProps {
  open: boolean;
  onOk: (selectedFile: File, customer_id: number, holes: number) => void;
  onCancel: () => void;
  organizer: ICustomer;
  type: "tournament" | "league";
} 

export default function ImportPlayersFileModal(props: IFileSelectProps) {
  const fileUploadRef = useRef(null);
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer>(null);
  const [customers, setCustomers] = useState<ICustomer[]>(undefined);
  const [customerQuery, setCustomerQuery] = useState<string>("");
  const dispatch = useAppDispatch();
  const [holes, setHoles] = useState<18 | 9>(18);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCustomers(customerQuery, source.token);
    return () => source.cancel();
  }, [customerQuery]);

  async function loadCustomers(customerSearchQuery: string, cancelToken: CancelToken) {
    setCustomers(undefined);

    const customerRes = await GetCustomer({ search: customerSearchQuery }, false, cancelToken);

    if (customerRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error getting customer")));
      return;
    }

    setCustomers(customerRes.data);
  }

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files;
    if (file && file.length > 0) {
      setSelectedFile(file[0]);
    }
  }

  function onOk() {
    if (!selectedFile || !props?.onOk) {
      return;
    }

    //If tournament/league organizer is set, use them. Else, use the selected customer from the dropdown
    void props?.onOk(selectedFile, selectedCustomer ? selectedCustomer?.id : props.organizer?.id, holes);

    fileUploadRef.current.value = "";
    setSelectedFile(null);
  }

  function onCancel() {
    ReactDOM.unstable_batchedUpdates(() => {
      setSelectedCustomer(null);
      setCustomerQuery("");
      fileUploadRef.current.value = "";
      setSelectedFile(null);
      setHoles(18);
      void props?.onCancel();
    });
  }

  return (
    <Sheet
      title={t("Import Players")}
      open={props.open}
      size="small"
      closable
      onCancel={onCancel}
      onOk={onOk}
      okText={t("Import")}
      okDisabled={!selectedFile || (!props?.organizer && !selectedCustomer) ? true : false}
      overflow
    >
      <Callout
        type="info"
        title={!selectedFile ? t("No file selected") : t("Selected File")}
        content={!selectedFile ? t("Select a file to continue") : selectedFile?.name}
      />

      <div className="file-import-container mt-2 mb-2">
        <input
          ref={fileUploadRef}
          className="file-import-input"
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
        />
        <FontAwesomeIcon className="file-import-icon" icon={["far", "upload"]} />
      </div>

      {!props?.organizer && (
        <>
          {selectedCustomer ? (
            <GolferCard
              closable
              removeGolfer={() => setSelectedCustomer(null)}
              email={selectedCustomer?.email}
              name={selectedCustomer?.full_name}
              memberCode={selectedCustomer?.member_code}
              customerType={selectedCustomer?.customer_type}
              phone={selectedCustomer?.phone}
            />
          ) : (
            <Select
              label={t("Organizer")}
              onChange={(value: string, extraValues: ICustomer) => setSelectedCustomer(extraValues)}
              showSearch
              searchValue={customerQuery}
              onSearch={(search: string) => setCustomerQuery(search)}
              searching={customers === undefined}
              placeholder={t("Search organizers...")}
              helpText={props.type === "tournament" ? t("No tournament organizer set - please select a tournament organizer") : t("No league organizer set - please select a league organizer")}
            >
              {customers?.map((customer, index) => (
                <Select.Option value={customer.id} key={index} extraValues={customer}>
                  <div className="flex justify-between">
                    <div>
                      <div className="font-semibold text-lg">{customer?.full_name}</div>
                      <div className="text-sm text-gray-500">{customer.customer_type}</div>
                      <div className="text-sm text-gray-500">{customer.email}</div>
                      <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                    </div>

                    <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          )}
          <br />
        </>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", width: "50%" }}>
        <label className="rc-select-dropdown-label">{t("Holes")}</label>
        <Radio.Group name="holes_edit" onChange={val => setHoles(Number(val) as 18 | 9)} value={holes}>
          <Radio.Button value={18}>18</Radio.Button>
          <Radio.Button value={9}>9</Radio.Button>
        </Radio.Group>
      </div>
    </Sheet>
  );
}
